import { createSlice } from '@reduxjs/toolkit';

import { ApiException, SlicePayload, ToastMessage, ToastOptions } from '@/types';
import { getApiErrorTranslationKey } from '@/utils/millesimaUtils';
import { processPayment } from '@/redux/thunks/checkout/processPayment';
import { validatePayment } from '@/redux/thunks/checkout/validatePayment';
import { getShippingList } from '@/redux/thunks/getShippingList';
import { deleteProductFromCart } from '@/redux/thunks/deleteProductFromCart';

export type ToastState = {
  toastsMessages: ToastMessage[];
  nextToastId: number;
}

export const initialState: ToastState = {
  toastsMessages: [],
  nextToastId: 1
};

type AddToastPayload = Omit<ToastMessage, 'id'>;

const toast = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    addToast: (state, action: SlicePayload<AddToastPayload>) => {
      buildMessages(state, action.payload);
    },

    addApiErrorToast: (state, action: SlicePayload<{ error: ApiException, options?: ToastOptions }>) => {
      buildMessages(state, {
        msg: getApiErrorTranslationKey(action.payload.error),
        err: true,
        apiError: {
          code: action.payload.error.error.errorCode,
          status: action.payload.error.status
        },
        options: action.payload.options
      });
    },

    removeToast: (state, action: SlicePayload<{ id: number }>) => {
      state.toastsMessages = state.toastsMessages.filter((t) => t.id !== action.payload.id);
    },

    resetToasts: (state) => {
      state.toastsMessages = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(processPayment.rejected, (state, action) => {
      state.toastsMessages = [];
      buildMessages(state, {
        msg: action.payload.message,
        err: true,
      });
    });

    builder.addCase(getShippingList.rejected, (state, action: SlicePayload<{ msg?: string }>) => {
      if (action.payload && action.payload.msg) {
        buildMessages(state, action.payload as AddToastPayload);
      }
    });

    builder.addCase(validatePayment.rejected, (state, action) => {
      state.toastsMessages = [];
      if (action.payload?.message) {
        buildMessages(state, {
          msg: action.payload.message,
          err: true,
        });
      }
    });

    builder.addCase(deleteProductFromCart.rejected, (state, action) => {
      state.toastsMessages = [];
      buildMessages(state, {
        msg: action.payload.message,
        err: true,
      });
    });
  }
});

const buildMessages = (state: ToastState, payload: AddToastPayload): void => {
  const { msg, err = true, apiError, options } = payload;
  // Filter the list to avoid to print several time the same toast
  state.toastsMessages = state.toastsMessages.filter((t) => t.msg !== payload.msg);
  state.toastsMessages.push({
    msg,
    err,
    apiError,
    options,
    id: state.nextToastId++
  });
};

export const {
  addToast, addApiErrorToast, removeToast, resetToasts
} = toast.actions;

export default toast.reducer;
